import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from './Button';

const About = () => {
  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            className="space-y-8 order-2 lg:order-1"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            {/* Label */}
            <motion.p
              className="text-sm font-semibold tracking-[0.2em] uppercase text-steel-lighter"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              About Us
            </motion.p>

            {/* Heading */}
            <motion.h2
              className="text-4xl lg:text-5xl xl:text-6xl font-bold text-steel leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Empowering industries{' '}
              <span className="relative inline-block">
                <span className="relative z-10">through</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-primary/20 -z-0"></span>
              </span>{' '}
              the power of{' '}
              <span className="relative inline-block">
                <span className="relative z-10">precision.</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-secondary/30 -z-0"></span>
              </span>
            </motion.h2>

            {/* Description */}
            <motion.p
              className="text-lg text-steel-lighter leading-relaxed max-w-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Alpha Laser Technologies is Coimbatore's trusted partner for precision laser cutting and custom metal fabrication. With 3+ years of experience, our state-of-the-art BODOR Fiber Laser machines and expert technicians deliver exceptional quality for industrial laser solutions across South India.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link to="/about">
                <Button variant="primary" size="lg" className="px-8 py-4 rounded-xl shadow-lg shadow-primary/30">
                  Learn More About Us
                </Button>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right - Image with Decorative Elements */}
          <motion.div
            className="relative order-1 lg:order-2"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            {/* Main Container */}
            <div className="relative w-full h-[320px] sm:h-[400px] lg:h-[550px]">
              {/* Background Blob - Dark Teal */}
              <div className="absolute top-8 left-0 w-[70%] h-[85%] bg-primary rounded-[40%_60%_70%_30%/40%_50%_60%_50%] z-0"></div>
              
              {/* Main Image with Circular Clip */}
              <div className="absolute top-0 right-0 w-[85%] h-full z-10">
                <div 
                  className="w-full h-full rounded-[50%_50%_45%_55%/45%_45%_55%_55%] overflow-hidden shadow-2xl"
                  style={{
                    clipPath: 'ellipse(85% 90% at 60% 50%)'
                  }}
                >
                  <img
                    src="https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/frame_007_delay-0.04s.webp"
                    alt="Professional welder performing precision welding at Alpha Laser Technologies Coimbatore"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Dotted Pattern - Bottom Left */}
              <div className="absolute -bottom-4 left-[5%] z-20">
                <div className="grid grid-cols-6 gap-2">
                  {[...Array(24)].map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-primary/30"></div>
                  ))}
                </div>
              </div>

              {/* Small Decorative Circles */}
              <motion.div 
                className="absolute bottom-8 right-8 w-4 h-4 rounded-full bg-gray-300 z-20"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              ></motion.div>
              <motion.div 
                className="absolute bottom-4 right-16 w-2 h-2 rounded-full bg-gray-200 z-20"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              ></motion.div>

              {/* Stats Badge */}
              <motion.div
                className="absolute -bottom-6 left-[15%] bg-white rounded-2xl p-4 shadow-xl border border-gray-100 z-30"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary to-red-600 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-steel">95%</p>
                    <p className="text-sm text-steel-lighter">Client Satisfaction</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default About;